/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2026 Rochester Software Associates (service@rocsoft.com)
 */

import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import useForceUpdate from '~utils/hooks/useForceUpdate';

import FormattedString from '../text/FormattedString';

import { AlertType } from './AlertType';
import Toast from './Toast';

const ToastContext = createContext(null);

// Keep this in-sync with the fade-out animation delay in the css, so the element is removed right when the animation finishes
const FADE_TIMEOUT = 200;

const DEFAULT_TIMEOUT = 15_000;

const MIN_OFFSET = 150;

let toastCount = 0;

const ToastProvider = ({ children }) => {
    const forceUpdate = useForceUpdate();
    const [toasts, setToasts] = useState([]);

    // Clears a single toast message when it times out
    const clearToast = useCallback(
        (toast) => {
            if (!toast.fading) {
                toast.fading = true;
                // We do this so something changes that will trigger a re-render when the fading animation starts
                forceUpdate();
                setTimeout(() => {
                    setToasts((currentToasts) => currentToasts.filter((e) => e !== toast));
                }, FADE_TIMEOUT);
            }
        },
        [setToasts, forceUpdate]
    );

    // Closes all toast messages of the same type (error, success, etc.) when the "X" is clicked
    const closeAllToasts = useCallback(
        (toast) => {
            setToasts((currentToasts) => currentToasts.map((e) => {
                if (e.type === toast.type) {
                    e.fading = true;
                }
                return e;
            }));
            // We do this so something changes that will trigger a re-render when the fading animation starts
            forceUpdate();
            setTimeout(() => {
                setToasts((currentToasts) => currentToasts.filter((e) => e.type !== toast.type));
            }, FADE_TIMEOUT);
        },
        [setToasts, forceUpdate]
    );

    const addToast = useCallback(
        ({ type, message, timeout = DEFAULT_TIMEOUT }) => {
            const key = toastCount++;
            const toast = { key, type, message, close: () => closeAllToasts(toast), fading: false };
            setToasts((currentToasts) => [...currentToasts, toast]);
            if (timeout !== 0 && type !== AlertType.USER_ERROR && type !== AlertType.SYSTEM_ERROR) {
                // User and system errors must be closed by the user
                setTimeout(() => clearToast(toast), timeout);
            }
            return toast;
        },
        [setToasts, clearToast, closeAllToasts]
    );

    const value = useMemo(
        () => ({
            addToast,
            clearToast,
        }),
        [addToast, clearToast]
    );

    const [isVisible, setIsVisible] = useState(false);
    const toggleHeight = useCallback(() => {
        setIsVisible(window.scrollY >= MIN_OFFSET);
    }, [setIsVisible]);

    useEffect(() => {
        window.addEventListener('scroll', toggleHeight);
        return () => {
            window.removeEventListener('scroll', toggleHeight);
        };
    }, [toggleHeight]);

    const classNames = ['rsa-toast-section', 'bottom'];
    if (isVisible) {
        classNames.push('scrolled-down');
    }

    return (
        <ToastContext.Provider value={value}>
            {children}
            {toasts.length > 0 && (
                <div className={classNames.join(' ')}>
                    {toasts.map((toast) => (
                        <Toast key={toast.key} type={toast.type} onDismiss={toast.close} fading={toast.fading}>
                            {toast.message}
                        </Toast>
                    ))}
                </div>
            )}
        </ToastContext.Provider>
    );
};

export const displayToastForAPIResponse = (addToast, response, label) => {
    if (response?.messages) {
        response.messages.map((message) => {
            addToast({
                type: message.type,
                message: (
                    <FormattedString
                        pattern={message.message}
                        source={`${label} response.messages.message`}
                    />
                ),
            });
        });
    }

    return response?.messages?.length > 0;
};

ToastProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

const useAddToast = () => {
    const { addToast } = useContext(ToastContext);
    return addToast;
};

const useClearToast = () => {
    const { clearToast } = useContext(ToastContext);
    return clearToast;
};

export { ToastProvider, useAddToast, useClearToast };
